/*
 * PRD-037 — Estado del comercio del borrador SIN Redux (app SEPARADA del partner).
 *
 * Espeja la superficie util de `useCommerce` del panel (`commerce` + `handleUpdateValue` +
 * `save`) pero sin el store de Redux: la app del partner no monta el Provider del panel. Carga y
 * persiste el borrador via `useFetchCallback` (GET/PUT `/v1/comercio`), que el interceptor de
 * axiosInstance autoriza con el JWT-draft que `useDraftSession` deja en localStorage.
 *
 * Debe montarse SOLO cuando la sesion-draft ya esta lista (token en localStorage); de lo contrario
 * el GET saldria sin scope al borrador.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Commerce } from '@interfaces/general';
import { useFetchCallback } from '@services/services';

interface DraftCommerceState {
  commerce: Commerce | null;
  loading: boolean;
  saving: boolean;
  loadError: boolean;
  handleUpdateValue: <K extends keyof Commerce>(key: K, value: Commerce[K]) => void;
  save: () => Promise<boolean>;
}

export const useDraftCommerce = (): DraftCommerceState => {
  const { call: fetchCommerce } = useFetchCallback<Commerce>('get', '/v1/comercio');
  const { call: putCommerce, isFetching: saving } = useFetchCallback<Commerce, Commerce>('put', '/v1/comercio');

  const [commerce, setCommerce] = useState<Commerce | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Espejo del commerce para que `save` lea siempre el ultimo valor sin re-crear el callback.
  const commerceRef = useRef<Commerce | null>(null);
  useEffect(() => {
    commerceRef.current = commerce;
  }, [commerce]);

  useEffect(() => {
    let active = true;
    fetchCommerce()
      .then((data) => {
        if (!active) return;
        setCommerce(data);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setLoadError(true);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [fetchCommerce]);

  const handleUpdateValue = useCallback(<K extends keyof Commerce>(key: K, value: Commerce[K]) => {
    setCommerce((prev) => (prev ? { ...prev, [key]: value } : prev));
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    const current = commerceRef.current;
    if (!current) return false;
    await putCommerce({ data: current });
    return true;
  }, [putCommerce]);

  return { commerce, loading, saving, loadError, handleUpdateValue, save };
};
